import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ScrollToTop from "@/components/base/ScrollToTop";
import SEOHelmet from "@/components/base/SEOHelmet";
import Breadcrumb from "@/components/base/Breadcrumb";
import { articles } from "@/mocks/articles";
import { videos, categories } from "@/mocks/videos";

export default function Article() {
  const { id } = useParams<{ id: string }>();

  const article = useMemo(() => articles.find((a) => a.id === id), [id]);
  const videoDetail = useMemo(() => (!article ? videos.find((v) => v.id === id) : null), [id, article]);

  const relatedArticles = useMemo(() => {
    if (!article) return [];
    return articles
      .filter((a) => a.id !== article.id && a.category === article.category)
      .slice(0, 3);
  }, [article]);

  const relatedVideos = useMemo(() => {
    if (!videoDetail) return [];
    return videos
      .filter((v) => v.id !== videoDetail.id && v.category === videoDetail.category)
      .slice(0, 6);
  }, [videoDetail]);

  const categoryMap = useMemo(() => {
    const map: Record<string, string> = {};
    categories.forEach((c) => {
      map[c.slug] = c.name;
    });
    return map;
  }, []);

  if (!article && !videoDetail) {
    return (
      <>
        <ScrollToTop />
        <div className="min-h-screen bg-background-50">
          <Navbar />
          <main className="pt-20 flex items-center justify-center min-h-[60vh]">
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 mx-auto mb-4">
                <i className="ri-error-warning-line text-2xl text-foreground-300"></i>
              </div>
              <h2 className="text-lg font-medium text-foreground-700 mb-2">内容未找到</h2>
              <p className="text-sm text-foreground-400 mb-6">该内容可能已下架或地址有误</p>
              <Link
                to="/"
                className="inline-flex items-center px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors"
              >
                返回首页
              </Link>
            </div>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  if (videoDetail) {
    const articleSchema = {
      "@context": "https://schema.org",
      "@type": "Movie",
      name: videoDetail.title,
      description: videoDetail.description,
      image: videoDetail.poster,
      dateCreated: `${videoDetail.year}`,
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: videoDetail.rating,
        bestRating: "10",
      },
    };

    return (
      <>
        <ScrollToTop />
        <SEOHelmet
          title={`${videoDetail.title} - 91看网页版在线观看`}
          description={`91看网页版提供${videoDetail.title}高清在线观看。${videoDetail.description} 无需下载安装，打开浏览器即可观看。`}
          keywords={`${videoDetail.title},91看网页版,91看网页版在线观看,${categoryMap[videoDetail.category] || ""},高清播放`}
          canonicalUrl={`https://www.91kan.com/article/${videoDetail.id}`}
          ogType="video.movie"
          ogImage={videoDetail.poster}
          schemaData={articleSchema}
        />

        <div className="min-h-screen bg-background-50">
          <Navbar />

          <main className="pt-20">
            <div className="max-w-4xl mx-auto px-4 md:px-8">
              <Breadcrumb
                items={[
                  { name: "首页", path: "/" },
                  { name: "分类浏览", path: "/category/all" },
                  { name: categoryMap[videoDetail.category] || videoDetail.category, path: `/category/${videoDetail.category}` },
                  { name: videoDetail.title },
                ]}
              />

              <article>
                <div className="flex flex-col md:flex-row gap-8 mb-12">
                  <div className="flex-shrink-0 w-full md:w-72">
                    <div className="aspect-[3/4] rounded-xl overflow-hidden">
                      <img
                        src={videoDetail.poster}
                        alt={videoDetail.title}
                        title={`${videoDetail.title} - 91看网页版在线观看`}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                  </div>

                  <div className="flex-1">
                    <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-4" style={{ fontFamily: "var(--font-heading)" }}>
                      {videoDetail.title}
                    </h1>

                    <div className="flex flex-wrap items-center gap-3 mb-4">
                      <div className="flex items-center gap-1 text-yellow-500">
                        <i className="ri-star-fill"></i>
                        <span className="text-sm font-semibold">{videoDetail.rating}</span>
                      </div>
                      <span className="text-foreground-300">|</span>
                      <span className="text-sm text-foreground-500">{videoDetail.year}</span>
                      <span className="text-foreground-300">|</span>
                      <span className="text-sm text-foreground-500">{videoDetail.duration}</span>
                      {videoDetail.isVip && (
                        <>
                          <span className="text-foreground-300">|</span>
                          <span className="px-2 py-0.5 rounded-full bg-accent-100 text-accent-600 text-xs font-medium">VIP</span>
                        </>
                      )}
                    </div>

                    <div className="flex flex-wrap gap-1.5 mb-6">
                      {videoDetail.tags.map((tag) => (
                        <span key={tag} className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium">
                          {tag}
                        </span>
                      ))}
                    </div>

                    <p className="text-sm text-foreground-600 leading-relaxed mb-8">
                      {videoDetail.description}
                    </p>

                    <div className="flex flex-col sm:flex-row gap-3">
                      <button className="flex items-center justify-center gap-2 px-8 py-3 rounded-full bg-primary-500 text-background-50 font-medium text-sm whitespace-nowrap hover:bg-primary-600 transition-colors duration-200">
                        <i className="ri-play-circle-line text-lg"></i>
                        立即播放
                      </button>
                      <button className="flex items-center justify-center gap-2 px-8 py-3 rounded-full border border-background-300 text-foreground-700 font-medium text-sm whitespace-nowrap hover:bg-background-100 transition-colors duration-200">
                        <i className="ri-heart-line text-lg"></i>
                        收藏
                      </button>
                      <button className="flex items-center justify-center gap-2 px-8 py-3 rounded-full border border-background-300 text-foreground-700 font-medium text-sm whitespace-nowrap hover:bg-background-100 transition-colors duration-200">
                        <i className="ri-share-line text-lg"></i>
                        分享
                      </button>
                    </div>
                  </div>
                </div>
              </article>

              {relatedVideos.length > 0 && (
                <section className="pb-14">
                  <h2 className="text-xl font-bold text-foreground-950 mb-6" style={{ fontFamily: "var(--font-heading)" }}>
                    同类推荐
                  </h2>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4" data-product-shop>
                    {relatedVideos.map((v) => (
                      <Link key={v.id} to={`/article/${v.id}`} className="group">
                        <div className="relative aspect-[3/4] rounded-lg overflow-hidden mb-3">
                          <img
                            src={v.poster}
                            alt={v.title}
                            title={`${v.title} - 91看网页版在线观看`}
                            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-400"
                          />
                          <div className="absolute top-2 left-2 flex items-center gap-1 px-2 py-0.5 rounded-md bg-black/60 text-yellow-400 text-xs">
                            <i className="ri-star-fill text-[10px]"></i>
                            {v.rating}
                          </div>
                        </div>
                        <h3 className="text-sm font-medium text-foreground-900 group-hover:text-primary-500 transition-colors duration-200 line-clamp-1">
                          {v.title}
                        </h3>
                        <p className="text-xs text-foreground-400 mt-1">{v.year}</p>
                      </Link>
                    ))}
                  </div>
                </section>
              )}
            </div>
          </main>

          <Footer />
        </div>
      </>
    );
  }

  if (article) {
    const articleSchema = {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: article.title,
      description: article.excerpt,
      image: article.cover,
      author: {
        "@type": "Person",
        name: article.author,
      },
      datePublished: article.publishDate,
      publisher: {
        "@type": "Organization",
        name: "91看网页版",
      },
    };

    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: "https://www.91kan.com/" },
        { "@type": "ListItem", position: 2, name: "影视资讯", item: "https://www.91kan.com/category/all" },
        { "@type": "ListItem", position: 3, name: article.title },
      ],
    };

    return (
      <>
        <ScrollToTop />
        <SEOHelmet
          title={`${article.title} - 91看网页版影视资讯`}
          description={article.excerpt}
          keywords={`${article.tags.join(",")},91看网页版,影视资讯`}
          canonicalUrl={`https://www.91kan.com/article/${article.id}`}
          ogType="article"
          ogImage={article.cover}
          schemaData={{ ...articleSchema, ...breadcrumbSchema }}
        />

        <div className="min-h-screen bg-background-50">
          <Navbar />

          <main className="pt-20">
            <div className="max-w-3xl mx-auto px-4 md:px-8">
              <Breadcrumb
                items={[
                  { name: "首页", path: "/" },
                  { name: "影视资讯", path: "/category/all" },
                  { name: article.title },
                ]}
              />

              <article>
                <div className="mb-8">
                  <div className="flex items-center gap-2 mb-4">
                    <span className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium">
                      {article.category}
                    </span>
                    <span className="text-xs text-foreground-400">{article.readTime}</span>
                  </div>
                  <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-4" style={{ fontFamily: "var(--font-heading)" }}>
                    {article.title}
                  </h1>
                  <div className="flex items-center gap-3 text-sm text-foreground-400">
                    <span>{article.author}</span>
                    <span>·</span>
                    <span>{article.publishDate}</span>
                  </div>
                </div>

                <div className="aspect-[16/9] rounded-xl overflow-hidden mb-8">
                  <img
                    src={article.cover}
                    alt={article.title}
                    title={article.title}
                    className="w-full h-full object-cover"
                  />
                </div>

                <div
                  className="prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed [&_h2]:text-xl [&_h2]:font-bold [&_h2]:text-foreground-950 [&_h2]:mt-10 [&_h2]:mb-4 [&_h2]:font-heading [&_p]:mb-4 [&_p]:leading-relaxed [&_strong]:text-foreground-900"
                  style={{ fontFamily: "var(--font-body)" } as React.CSSProperties}
                  dangerouslySetInnerHTML={{ __html: article.content }}
                ></div>

                <div className="flex flex-wrap gap-1.5 mt-8 pt-6 border-t border-background-200">
                  {article.tags.map((tag) => (
                    <span key={tag} className="px-3 py-1 rounded-full bg-background-100 text-foreground-500 text-xs">
                      #{tag}
                    </span>
                  ))}
                </div>
              </article>

              {relatedArticles.length > 0 && (
                <section className="mt-12 pb-14">
                  <h2 className="text-xl font-bold text-foreground-950 mb-6" style={{ fontFamily: "var(--font-heading)" }}>
                    相关推荐
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                    {relatedArticles.map((a) => (
                      <Link
                        key={a.id}
                        to={`/article/${a.id}`}
                        className="group rounded-xl bg-background-100 overflow-hidden"
                      >
                        <div className="aspect-[16/9] overflow-hidden">
                          <img
                            src={a.cover}
                            alt={a.title}
                            title={a.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-400"
                          />
                        </div>
                        <div className="p-4">
                          <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors duration-200 line-clamp-2 mb-2">
                            {a.title}
                          </h3>
                          <span className="text-xs text-foreground-400">{a.publishDate}</span>
                        </div>
                      </Link>
                    ))}
                  </div>
                </section>
              )}
            </div>
          </main>

          <Footer />
        </div>
      </>
    );
  }

  return null;
}